import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import { Link } from 'react-router-dom';
import type { RentalMachineStatus } from '../../types/rental';

const th: React.CSSProperties = {
  padding: '10px 16px',
  textAlign: 'left',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  borderBottom: '1px solid rgba(92,78,51,0.15)',
  whiteSpace: 'nowrap',
};

const td: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid rgba(239,232,213,0.6)',
  fontFamily: "'Inter', sans-serif",
  fontSize: 13,
  color: '#5b5a52',
  whiteSpace: 'nowrap',
};

const inputStyle: React.CSSProperties = {
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '9px 12px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  width: '100%',
};

export const MachineStatusList: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [customerFilter, setCustomerFilter] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['rented-machines', { status: statusFilter || undefined }],
    queryFn: () => rentalService.getRentedMachines({
      status: statusFilter || undefined
    })
  });

  const machines = data?.data || [];

  const filteredMachines = machines.filter(machine => {
    if (!customerFilter) return true;
    return machine.customer?.name?.toLowerCase().includes(customerFilter.toLowerCase());
  });

  const getStatusStyle = (status: RentalMachineStatus): React.CSSProperties => {
    switch (status) {
      case 'RENTED':
        return { background: 'rgba(100,200,100,0.12)', color: '#6fc96f', border: '1px solid rgba(100,200,100,0.25)' };
      case 'RETURNED':
        return { background: 'rgba(92,78,51,0.2)', color: '#d97706', border: '1px solid rgba(92,78,51,0.35)' };
      case 'MAINTENANCE':
        return { background: 'rgba(220,180,60,0.12)', color: '#dbb840', border: '1px solid rgba(220,180,60,0.25)' };
      default:
        return { background: 'rgba(92,78,51,0.15)', color: '#6b6a5c', border: '1px solid rgba(92,78,51,0.25)' };
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={{ height: 48, background: 'rgba(239,232,213,0.5)', borderRadius: 4, opacity: 0.6 }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 32, textAlign: 'center' }}>
        <p style={{ color: '#c0504d', fontFamily: "'Inter', sans-serif", fontSize: 13 }}>Error loading machines. Please try again.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontFamily: "'Inter', sans-serif", fontSize: 28, fontWeight: 600, color: '#1c2033', margin: 0 }}>
          Machine Status
        </h1>
      </div>

      {/* Main Card */}
      <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8 }}>
        {/* Filters Row */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(92,78,51,0.15)', display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {/* Customer Search */}
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by customer..."
                value={customerFilter}
                onChange={(e) => setCustomerFilter(e.target.value)}
                style={{ ...inputStyle, paddingLeft: 34, width: 200 }}
              />
              <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#6b6a5c', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={inputStyle}
            >
              <option value="">All Status</option>
              <option value="RENTED">Rented</option>
              <option value="RETURNED">Returned</option>
              <option value="MAINTENANCE">Maintenance</option>
            </select>
          </div>

          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>
            Showing {filteredMachines.length} machine(s)
          </span>
        </div>

        {filteredMachines.length === 0 ? (
          <div style={{ padding: '64px 24px', textAlign: 'center' }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: 20, color: '#6b6a5c', marginBottom: 8 }}>No machines found</div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 13, color: '#6b6a5c', margin: 0 }}>Try adjusting your filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead style={{ background: '#faf7ee' }}>
                <tr>
                  <th style={th}>Serial Number</th>
                  <th style={th}>Product</th>
                  <th style={th}>Customer</th>
                  <th style={th}>Contract #</th>
                  <th style={th}>Contract Period</th>
                  <th style={th}>Status</th>
                  <th style={th}>Return Info</th>
                </tr>
              </thead>
              <tbody>
                {filteredMachines.map((item, index) => (
                  <tr
                    key={item.machine?.id || item.serialNumber?.id || index}
                    style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(241,245,249,1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <td style={td}>
                      <span style={{ color: '#1c2033', fontWeight: 500 }}>
                        {item.serialNumber?.serialNumber || 'N/A'}
                      </span>
                    </td>
                    <td style={td}>{item.serialNumber?.product?.name || 'N/A'}</td>
                    <td style={td}>
                      <div style={{ color: '#1c2033' }}>{item.customer?.name || 'N/A'}</div>
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>{item.customer?.email || 'N/A'}</div>
                    </td>
                    <td style={td}>
                      <Link
                        to={`/rentals/${item.contract?.id || ''}`}
                        style={{ color: '#d97706', textDecoration: 'none', fontFamily: "'Inter', sans-serif", fontSize: 13 }}
                        onMouseEnter={e => (e.currentTarget.style.color = '#fbbf24')}
                        onMouseLeave={e => (e.currentTarget.style.color = '#d97706')}
                      >
                        {item.contract?.contractNumber || 'N/A'}
                      </Link>
                    </td>
                    <td style={td}>
                      <div style={{ color: '#5b5a52' }}>
                        {item.contract?.startDate ? new Date(item.contract.startDate).toLocaleDateString() : 'N/A'}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                        to {item.contract?.endDate ? new Date(item.contract.endDate).toLocaleDateString() : 'N/A'}
                      </div>
                    </td>
                    <td style={td}>
                      <span style={{
                        ...getStatusStyle(item.machine?.status || 'RENTED'),
                        padding: '3px 10px',
                        borderRadius: 12,
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 9,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        display: 'inline-block',
                      }}>
                        {item.machine?.status || 'RENTED'}
                      </span>
                    </td>
                    <td style={td}>
                      {item.machine?.returnDate ? (
                        <div>
                          <div style={{ color: '#5b5a52' }}>{new Date(item.machine.returnDate).toLocaleDateString()}</div>
                          {item.machine?.returnCondition && (
                            <div style={{ fontSize: 11, color: '#6b6a5c', marginTop: 2 }}>
                              Condition: {item.machine.returnCondition}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#6b6a5c' }}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
        {/* Currently Rented */}
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: 0, marginBottom: 8 }}>Currently Rented</p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 32, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              {machines.filter(m => m.machine?.status === 'RENTED').length}
            </p>
          </div>
          <div style={{ fontSize: 28, opacity: 0.6 }}>🖨️</div>
        </div>

        {/* Returned */}
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: 0, marginBottom: 8 }}>Returned</p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 32, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              {machines.filter(m => m.machine?.status === 'RETURNED').length}
            </p>
          </div>
          <div style={{ fontSize: 28, opacity: 0.6 }}>✅</div>
        </div>

        {/* In Maintenance */}
        <div style={{ background: '#ffffff', border: '1px solid rgba(92,78,51,0.15)', borderRadius: 8, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 9, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#6b6a5c', margin: 0, marginBottom: 8 }}>In Maintenance</p>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 32, fontWeight: 600, color: '#1c2033', margin: 0 }}>
              {machines.filter(m => m.machine?.status === 'MAINTENANCE').length}
            </p>
          </div>
          <div style={{ fontSize: 28, opacity: 0.6 }}>🔧</div>
        </div>
      </div>
    </div>
  );
};
